import { useState } from 'react';

import PropTypes from 'prop-types';

import { FormattedDate, FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Checkbox from '@zenbusiness/zui/Checkbox';
import Divider from '@zenbusiness/zui/Divider';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import { billingPaymentMethods } from '#client/constants/routes';

import { getRestartMessages } from './messages';

import { getProductNameMessageId } from '../SubscriptionCancel/constants';

export default function RestartConfirmStep({ subscription, paymentMethod, onSubmit, submitting }) {
  const theme = useTheme();
  const { formatMessage, formatNumber } = useIntl();
  const [consentChecked, setConsentChecked] = useState(false);

  const msg = getRestartMessages(subscription.inTrial);
  const dateValue = subscription.inTrial ? subscription.termStartDate : subscription.termEndDate;
  const rawDisplayName = subscription.summary || subscription.service;
  const productNameMessageId =
    subscription.titleMessageId || getProductNameMessageId(subscription.service, subscription.summary);

  const displayPrice = formatNumber(subscription.price ?? 0, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const cardInfo = paymentMethod ? `${paymentMethod.brand} x${paymentMethod.last4}` : '';

  return (
    <>
      <Stack gap={1.5}>
        <Text size="2xl" weight="bold" family="secondary">
          <FormattedMessage id={msg.heading} />
        </Text>
        <Text size="md" color="secondary">
          <FormattedMessage id={msg.intro} />
        </Text>
      </Stack>

      <Stack gap={3}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: theme.shape.rounded['2xl'],
            boxShadow: theme.shadows[1],
            overflow: 'hidden',
            py: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" gap={2} px={3} py={2}>
            <Box flex={1}>
              <Text size="sm" weight="bold">
                {productNameMessageId ? <FormattedMessage id={productNameMessageId} /> : rawDisplayName}
              </Text>
              <Text size="xs" color="secondary">
                <FormattedMessage
                  id="dashboard.subscriptionCancel.subscription.period.label"
                  values={{ period: subscription.period }}
                />
              </Text>
            </Box>
            <Box textAlign="right">
              <Text size="sm">
                <Text component="span" weight="bold">
                  {displayPrice}
                </Text>
                <Text component="span">
                  {' / '}
                  <FormattedMessage
                    id="dashboard.subscriptionCancel.subscription.interval.label"
                    values={{ period: subscription.period }}
                  />
                </Text>
              </Text>
            </Box>
          </Stack>

          <Divider sx={{ mx: 0 }} />

          <Stack direction="column" alignItems="flex-start" gap={0.5} px={3} py={2}>
            <Text size="xs" color="secondary">
              <FormattedMessage id={msg.nextPaymentLabel} />
            </Text>
            <Text size="sm" weight="bold">
              {dateValue ? (
                <FormattedDate value={new Date(dateValue)} month="2-digit" day="2-digit" year="numeric" />
              ) : null}
            </Text>
            <Text size="xs" color="tertiary">
              <FormattedMessage id={msg.nextPaymentCopy} />
            </Text>
          </Stack>

          <Divider sx={{ mx: 0 }} />

          <Stack direction="row" alignItems="center" gap={2} px={3} py={2}>
            <Box>
              <Text size="xs" color="secondary">
                <FormattedMessage id="dashboard.subscriptionRestart.confirm.paymentMethodLabel" />
              </Text>
              <Text size="sm" weight="bold">
                {cardInfo}
              </Text>
            </Box>
            <Box flex={1} textAlign="right">
              <Button
                variant="text"
                size="small"
                to={billingPaymentMethods}
                sx={{ color: 'primary.main', minWidth: 0, padding: 0 }}
              >
                <FormattedMessage id="dashboard.subscriptionRestart.confirm.changePayment" />
              </Button>
            </Box>
          </Stack>
        </Box>

        <Stack gap={0.5}>
          <FormControlLabel
            control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />}
            label={<FormattedMessage id={msg.consentCheckboxLabel} />}
          />
          <Box pl={5}>
            <Text size="xs" color="tertiary">
              <FormattedMessage id={msg.consentDisclaimer} values={{ period: subscription.period }} />
            </Text>
          </Box>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{
          [theme.breakpoints.down('sm')]: {
            '& > *': { width: '100%' },
          },
        }}
      >
        <Button
          variant="contained"
          size="large"
          disabled={!consentChecked || submitting}
          onClick={onSubmit}
          data-testid="restart-subscription-submit-button"
          sx={{
            borderRadius: '12px',
            height: 56,
          }}
        >
          {submitting ? (
            <LoadingSpinner aria-label={formatMessage({ id: msg.submittingAriaLabel })} size={24} />
          ) : (
            <FormattedMessage id={msg.submitButton} />
          )}
        </Button>
      </Stack>
    </>
  );
}

RestartConfirmStep.propTypes = {
  subscription: PropTypes.shape({
    service: PropTypes.string,
    summary: PropTypes.string,
    titleMessageId: PropTypes.string,
    price: PropTypes.number,
    period: PropTypes.string,
    inTrial: PropTypes.bool,
    termStartDate: PropTypes.string,
    termEndDate: PropTypes.string,
  }).isRequired,
  paymentMethod: PropTypes.shape({
    brand: PropTypes.string,
    last4: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
};
